import { self } from '@default/js/config';

const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

export const toAbsoluteUrl = (url) => {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : self + url;
};

const pushTag = (tags, property, content) => {
  if (content !== undefined && content !== null && content !== '') {
    tags.push({ property, content: String(content) });
  }
};

const buildImageTags = (tags, images) => {
  toArray(images).forEach((img) => {
    pushTag(tags, 'og:image', toAbsoluteUrl(img.url));
    pushTag(tags, 'og:image:secure_url', toAbsoluteUrl(img.secureUrl));
    pushTag(tags, 'og:image:type', img.type);
    pushTag(tags, 'og:image:width', img.width);
    pushTag(tags, 'og:image:height', img.height);
    pushTag(tags, 'og:image:alt', img.alt);
  });
};

const buildVideoTags = (tags, videos) => {
  toArray(videos).forEach((v) => {
    pushTag(tags, 'og:video', toAbsoluteUrl(v.url));
    pushTag(tags, 'og:video:secure_url', toAbsoluteUrl(v.secureUrl));
    pushTag(tags, 'og:video:type', v.type);
    pushTag(tags, 'og:video:width', v.width);
    pushTag(tags, 'og:video:height', v.height);
  });
};

const buildAudioTags = (tags, audios) => {
  toArray(audios).forEach((a) => {
    pushTag(tags, 'og:audio', toAbsoluteUrl(a.url));
    pushTag(tags, 'og:audio:secure_url', toAbsoluteUrl(a.secureUrl));
    pushTag(tags, 'og:audio:type', a.type);
  });
};

const buildTypeTags = (tags, type, props) => {
  const { articleMeta, bookMeta, profileMeta, videoMeta, musicSongMeta, musicAlbumMeta } = props;

  if ((type === 'article') && articleMeta) {
    pushTag(tags, 'article:published_time', articleMeta.publishedTime);
    pushTag(tags, 'article:modified_time', articleMeta.modifiedTime);
    pushTag(tags, 'article:expiration_time', articleMeta.expirationTime);
    toArray(articleMeta.author).forEach((a) => pushTag(tags, 'article:author', a));
    pushTag(tags, 'article:section', articleMeta.section);
    toArray(articleMeta.tags).forEach((t) => pushTag(tags, 'article:tag', t));
  }

  if (type === 'book' && bookMeta) {
    toArray(bookMeta.author).forEach((a) => pushTag(tags, 'book:author', a));
    pushTag(tags, 'book:isbn', bookMeta.isbn);
    pushTag(tags, 'book:release_date', bookMeta.releaseDate);
    toArray(bookMeta.tags).forEach((t) => pushTag(tags, 'book:tag', t));
  }

  if (type === 'profile' && profileMeta) {
    pushTag(tags, 'profile:first_name', profileMeta.firstName);
    pushTag(tags, 'profile:last_name', profileMeta.lastName);
    pushTag(tags, 'profile:username', profileMeta.username);
    pushTag(tags, 'profile:gender', profileMeta.gender);
  }

  const videoTypes = ['video.movie', 'video.episode', 'video.tv_show', 'video.other'];
  if (videoTypes.includes(type) && videoMeta) {
    toArray(videoMeta.actor).forEach((a) => pushTag(tags, 'video:actor', a));
    pushTag(tags, 'video:actor:role', videoMeta.actorRole);
    toArray(videoMeta.director).forEach((d) => pushTag(tags, 'video:director', d));
    toArray(videoMeta.writer).forEach((w) => pushTag(tags, 'video:writer', w));
    pushTag(tags, 'video:duration', videoMeta.duration);
    pushTag(tags, 'video:release_date', videoMeta.releaseDate);
    toArray(videoMeta.tags).forEach((t) => pushTag(tags, 'video:tag', t));
    if (type === 'video.episode') {
      pushTag(tags, 'video:series', videoMeta.series);
    }
  }

  if (type === 'music.song' && musicSongMeta) {
    pushTag(tags, 'music:duration', musicSongMeta.duration);
    pushTag(tags, 'music:album', musicSongMeta.album);
    pushTag(tags, 'music:album:disc', musicSongMeta.albumDisc);
    pushTag(tags, 'music:album:track', musicSongMeta.albumTrack);
    toArray(musicSongMeta.musician).forEach((m) => pushTag(tags, 'music:musician', m));
  }

  if ((type === 'music.album' || type === 'music.playlist') && musicAlbumMeta) {
    pushTag(tags, 'music:song', musicAlbumMeta.song);
    pushTag(tags, 'music:song:disc', musicAlbumMeta.songDisc);
    pushTag(tags, 'music:song:track', musicAlbumMeta.songTrack);
    pushTag(tags, 'music:musician', musicAlbumMeta.musician);
    if (type === 'music.album') {
      pushTag(tags, 'music:release_date', musicAlbumMeta.releaseDate);
    }
  }
};

export const stripHtml = (html, maxLength = 160) => {
  if (!html) return undefined;
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > maxLength ? text.slice(0, maxLength).trimEnd() + '…' : text;
};

export const buildOGTags = (props) => {
  const {
    title, type, url,
    description, siteName, locale, determiner, alternateLocales,
    image, video, audio,
  } = props;

  const tags = [];

  pushTag(tags, 'og:title', title);
  pushTag(tags, 'og:type', type);
  pushTag(tags, 'og:url', toAbsoluteUrl(url));
  pushTag(tags, 'og:description', description);
  pushTag(tags, 'og:site_name', siteName);
  pushTag(tags, 'og:locale', locale);
  pushTag(tags, 'og:determiner', determiner);

  buildImageTags(tags, image);
  buildVideoTags(tags, video);
  buildAudioTags(tags, audio);

  toArray(alternateLocales).forEach((loc) => pushTag(tags, 'og:locale:alternate', loc));

  buildTypeTags(tags, type, props);

  return tags;
};
